import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { orderBy } from 'lodash/fp'
import { useEffect, useState } from 'react'
import useOnPremAgentClient from '~/api/OnPremAgentClient/useOnPremAgentClient'
import { Dataset, DatasetFile } from '~/api/onPremAgent/datasetsAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import { FullPageError } from '~/components/Errors'
import LoadingIcon from '~/components/icons/LoadingIcon'
import DatasetBreadcrumbs from '../DatasetBreadcrumbs'
import {
  DATASET_STATUS_CONFIG,
  getDatasetSummary,
  getPlateBarcode,
  getRelativeDetectedTime,
} from '../DatasetsTable'
import DatasetFilesTable from './DatasetFilesTable'
import cs from './dataset_page.scss'

export default function DatasetPage({ datasetUuid }: { datasetUuid: string }) {
  const [uploadHasCompleted, setUploadHasCompleted] = useState(false)
  const client = useOnPremAgentClient()

  const {
    data: dataset,
    error: datasetError,
    isLoading: isDatasetLoading,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEYS.DATASET_DETAIL, datasetUuid],
    queryFn: async () => {
      return client.datasets.getDataset(datasetUuid)
    },
  })

  let {
    error: datasetFilesError,
    data: datasetFiles,
    isLoading: isDatasetFilesLoading,
  } = useQuery({
    queryFn: async () => client.datasets.getDatasetFiles(datasetUuid),
    queryKey: [QUERY_KEYS.DATASET_FILES, datasetUuid],
    refetchInterval: uploadHasCompleted ? false : 5000,
  })

  useEffect(() => {
    if (datasetFiles) {
      const allFilesUploaded = datasetFiles.every(file => file.status === 'uploaded')
      if (allFilesUploaded) {
        setUploadHasCompleted(true)
        void refetch()
      }
    }
  }, [datasetFiles])

  if (datasetError || datasetFilesError) {
    return <FullPageError error={(datasetError as Error) || datasetFilesError} />
  }

  return (
    <div className={cs.datasetPage}>
      <div className={cs.topBar}>
        <div className={cs.controls}>
          <DatasetBreadcrumbs dataset={dataset} />
        </div>
      </div>
      {(isDatasetLoading || isDatasetFilesLoading) && (
        <div className={cs.datasetLoading}>
          <LoadingIcon className={cs.icon} />
        </div>
      )}
      {!isDatasetFilesLoading && !isDatasetFilesLoading && dataset && datasetFiles && (
        <Content dataset={dataset} datasetFiles={datasetFiles} isPolling={true} />
      )}
    </div>
  )
}

function Content(props: {
  dataset: Dataset
  datasetFiles: DatasetFile[]
  isPolling?: boolean
}) {
  const { dataset, datasetFiles } = props

  const sortedDatasetFiles = orderBy('updated_at', 'desc', datasetFiles)

  const filesUploaded = sortedDatasetFiles.filter(file => file.status === 'uploaded')

  const _filesUploadFailed = sortedDatasetFiles.filter(
    file => file.status === 'upload_failed',
  )

  const getUploadStatusClass = () => {
    return DATASET_STATUS_CONFIG[dataset.status]?.type || 'info'
  }

  return (
    <div className={cs.content}>
      <div className={cx(cs.header, cs[getUploadStatusClass()])}>
        <div className={cs.datasetInfo}>
          <div className={cs.left}>
            <div className={cs.plateBarcode}>
              {getPlateBarcode(dataset) || dataset.uuid}
            </div>
            <div className={cs.status}>
              <div>
                Status: <span className={cs.statusLabel}>{dataset.status}</span>
              </div>
            </div>
          </div>
          <div className={cs.spacer} />
          <div className={cs.right}>
            <div className={cs.datasetSummary}>{getDatasetSummary(dataset)}</div>
            <div className={cs.ingestorSummary}>
              {dataset.instrument_name} &middot; Detected{' '}
              {getRelativeDetectedTime(dataset)}
            </div>
          </div>
        </div>
        <div className={cx(cs.runStatusBar, cs.loading)}>
          <div
            className={cs.runStatusBarInner}
            style={{ width: `${(filesUploaded.length / datasetFiles.length) * 100}%` }}
          />
        </div>
        <div className={cs.summary}>
          {filesUploaded.length} / {datasetFiles.length} files uploaded
        </div>
      </div>
      <div className={cs.tableWrapper}>
        <DatasetFilesTable datasetFiles={sortedDatasetFiles} />
      </div>
    </div>
  )
}
